/**
 * Typed client for the skills API (`/api/skills`): the skill catalog, the skill graph, career skill
 * maps, the foundations pathway and the learner's own mastery, plus the skill-focused interview
 * practice session (`POST /api/interview/sessions {skillId}`).
 *
 * Everything here is read only. Mastery changes only when the learner completes work, and the
 * server is the only writer; these shapes mirror `packages/server/src/services/skills.ts` with
 * anything the server may omit marked optional so a missing field never crashes a page.
 */
import { request } from './client';
import type { InterviewSessionView } from './types';
import type { MasteryState, ProgressionTier, SkillCounters } from '@worksim/engine';

export type { MasteryState, SkillCounters };

const enc = encodeURIComponent;

/** Public fields of a skill (`GET /skills`). */
export interface SkillSummary {
  id: string;
  name: string;
  description: string;
  category: string;
  domain?: string;
  foundation: boolean;
  plainEnglish?: string;
  whyItMatters?: string;
  prerequisiteSkillIds: string[];
  relatedSkillIds: string[];
  aliases: string[];
}

export interface SkillGraphNodeView {
  key: string;
  kind: string;
  id: string;
  title: string;
}

export interface SkillGraphEdgeView {
  from: string;
  to: string;
  kind: string;
  tier?: ProgressionTier;
}

/** The graph as the server sends it: nodes as `[kind, id, title]`, edges as index triples or quads. */
export interface SkillGraphWire {
  nodes: [string, string, string][];
  edges: number[][];
  edgeKinds: string[];
  tiers: ProgressionTier[];
  counts: { nodes: number; edges: number; byKind: Record<string, number> };
}

/** Expand the wire form into keyed nodes and edges. */
export function expandGraph(w: SkillGraphWire): SkillGraphView {
  const nodes: SkillGraphNodeView[] = w.nodes.map(([kind, id, title]) => ({ key: `${kind}:${id}`, kind, id, title }));
  const edges: SkillGraphEdgeView[] = w.edges.map((raw) => {
    const [f = -1, t = -1, k = -1, tier] = raw;
    const e: SkillGraphEdgeView = { from: nodes[f]?.key ?? '', to: nodes[t]?.key ?? '', kind: w.edgeKinds[k] ?? '' };
    if (tier !== undefined && w.tiers[tier]) e.tier = w.tiers[tier];
    return e;
  });
  return { nodes, edges, counts: w.counts };
}

/** `GET /skills/graph`: nodes keyed `kind:id`; content edges point at the skill. */
export interface SkillGraphView {
  nodes: SkillGraphNodeView[];
  edges: SkillGraphEdgeView[];
  counts: { nodes: number; edges: number; byKind: Record<string, number> };
}

export interface CareerSkillMapSkillView {
  skillId: string;
  name: string;
  domain?: string;
  foundation?: boolean;
  prerequisiteSkillIds: string[];
}

/** `GET /skills/career/:careerId/map`. Overlap between careers, never a ranking. */
export interface CareerSkillMapView {
  careerId: string;
  careerName: string;
  tiers: { tier: ProgressionTier; skills: CareerSkillMapSkillView[] }[];
  foundationSkillIds: string[];
  sharedWith: { careerId: string; careerName: string; sharedSkillIds: string[] }[];
}

/** One foundations pathway step (`GET /skills/foundations`); state fields only when signed in. */
export interface FoundationSkillView {
  order: number;
  skillId: string;
  name: string;
  domain?: string;
  plainEnglish: string;
  whyItMatters: string;
  prerequisiteSkillIds: string[];
  state?: MasteryState;
  label?: string;
  mastery?: number;
}

/** A learner's snapshot row (`GET /skills/mastery`). */
export interface MasteryRowView {
  skillId: string;
  name: string;
  category: string;
  domain?: string;
  foundation: boolean;
  tier?: ProgressionTier;
  state: MasteryState;
  label: string;
  mastery: number;
  counters: SkillCounters;
  lastEvidenceAt?: string;
  updatedAt?: string;
}

export interface MasteryResponse {
  rows: MasteryRowView[];
  counts: Record<MasteryState, number>;
  notice: string;
}

/** A state change (`GET /skills/mastery/history`), newest first. */
export interface MasteryHistoryEntry {
  skillId: string;
  name: string;
  from?: MasteryState;
  fromLabel?: string;
  to: MasteryState;
  toLabel: string;
  mastery: number;
  source: string;
  sourceId: string;
  at: string;
}

export interface MasteryHistoryResponse {
  history: MasteryHistoryEntry[];
  notice: string;
}

/**
 * A mastery change as completion responses carry it (`masteryChanges` on lesson submit, lab,
 * code lab and VM lab completion, scenario evaluation, project completion and interview completion).
 */
export interface MasteryChange {
  skillId: string;
  name: string;
  from?: MasteryState;
  to: MasteryState;
  direction: 'up' | 'down';
  mastery: number;
  source: string;
  sourceId: string;
  at: string;
  message: string;
}

export interface UsageRef {
  id: string;
  title: string;
}

export interface SkillUsageView {
  careers: { careerId: string; name: string; tier: ProgressionTier }[];
  lessons: UsageRef[];
  labs: UsageRef[];
  scenarios: UsageRef[];
  projects: UsageRef[];
  certifications: { certificationId: string; name: string; objectiveIds: string[] }[];
  interviewQuestionCount: number;
  practiceBanks: UsageRef[];
  prerequisites: { id: string; name: string }[];
  dependents: { id: string; name: string }[];
  related: { id: string; name: string }[];
  /** Knowledge topics, cheat sheets, resources, code labs, VM labs and assessments, by graph node kind. */
  extras: Record<string, UsageRef[]>;
}

export interface SkillMasteryDetail extends MasteryRowView {
  description: string;
  nextEvidence: string;
}

export type SkillNextKind = 'lesson' | 'knowledge-topic' | 'lab' | 'code-lab' | 'vm-lab' | 'scenario' | 'project';

export interface SkillNextStep {
  kind: SkillNextKind;
  id: string;
  title: string;
  reason: string;
}

export interface SkillEvidenceView {
  kind: string;
  id: string;
  title: string;
  score?: number;
  at: string;
}

/** `GET /skills/:id`: mastery, history and evidence are null or empty when signed out. */
export interface SkillDetailResponse {
  skill: SkillSummary;
  usage: SkillUsageView;
  certificationReadiness: { objectives: number; practiced: number };
  mastery: SkillMasteryDetail | null;
  history: { changes: number; recent: MasteryHistoryEntry[] } | null;
  evidence: SkillEvidenceView[];
  next: SkillNextStep | null;
  notice: string;
}

export interface SkillCertificationsResponse {
  skillId: string;
  certifications: {
    certificationId: string;
    name: string;
    issuer: string;
    objectives: { domainId: string; domainName: string; id: string; text: string; practiced?: boolean }[];
  }[];
  notice: string;
}

/** Interview questions that name the skill: prompt and kind only, never the rubric or guidance. */
export interface SkillInterviewQuestionsResponse {
  skillId: string;
  sessionKitId: string;
  questions: { kitId: string; careerId: string; careerName: string; kitTitle: string; questionId: string; kind: string; prompt: string }[];
  notice: string;
}

const query = (params: Record<string, string | number | undefined>): string => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : '';
};

export const skills = {
  /** Every skill with its public fields. */
  list: () => request<{ skills: SkillSummary[] }>('GET', '/skills'),
  /** The whole skill graph (cached by the server for five minutes), expanded from its compact wire form. */
  graph: async () => ({ graph: expandGraph((await request<{ graph: SkillGraphWire }>('GET', '/skills/graph')).graph) }),
  /** One career's skills per tier in prerequisite order, and the careers it shares skills with. */
  careerMap: (careerId: string) => request<{ map: CareerSkillMapView }>('GET', `/skills/career/${enc(careerId)}/map`),
  /** The "Must know before you work in tech" pathway, with the learner's state when signed in. */
  foundations: () => request<{ skills: FoundationSkillView[]; notice: string }>('GET', '/skills/foundations'),
  /** The learner's own snapshot; with `career`, every skill the career expects is listed. */
  mastery: (opts: { career?: string; state?: MasteryState } = {}) => request<MasteryResponse>('GET', `/skills/mastery${query(opts)}`),
  /** The learner's state changes, newest first (limit 1..200). */
  history: (opts: { skill?: string; limit?: number } = {}) => request<MasteryHistoryResponse>('GET', `/skills/mastery/history${query(opts)}`),
  /** One skill: where it is used, and the learner's mastery, history and next step when signed in. */
  detail: (id: string) => request<SkillDetailResponse>('GET', `/skills/${enc(id)}`),
  /** Certification objectives that name the skill (paraphrased study maps). */
  certifications: (id: string) => request<SkillCertificationsResponse>('GET', `/skills/${enc(id)}/certifications`),
  /** Interview questions that name the skill (prompt only). */
  interviewQuestions: (id: string) => request<SkillInterviewQuestionsResponse>('GET', `/skills/${enc(id)}/interview-questions`),
  /**
   * Start, or resume, a skill-focused interview practice session. 409 `interview_locked` until the
   * skill is at least practicing; 404 when no question names the skill.
   */
  startInterview: (skillId: string) => request<{ session: InterviewSessionView }>('POST', '/interview/sessions', { skillId, mode: 'practice' }),
};
